import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Shield,
  Plus,
  Search,
  Edit,
  Trash2,
  CheckCircle2,
  Clock,
  Users,
  FileText,
  Eye,
  Settings2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import CategoryManager from "../components/CategoryManager";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TermsConditions() {
  const [terms, setTerms] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingTerms, setEditingTerms] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    document_type: "terms_of_service",
    title: "",
    content: "",
    version: "1.0",
    effective_date: "",
    requires_acceptance: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("token");
    try {
      const [termsRes, typesRes] = await Promise.all([
        fetch(`${API}/api/security/terms?active_only=false`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/security/terms/types`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      
      if (termsRes.ok) {
        const data = await termsRes.json();
        setTerms(data.terms || []);
      }
      if (typesRes.ok) {
        const data = await typesRes.json();
        setTypes(data.types || []);
      }
    } catch (err) {
      toast.error("Failed to load terms data");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem("token");
    try {
      const method = editingTerms ? "PUT" : "POST";
      const url = editingTerms 
        ? `${API}/api/security/terms/${editingTerms.id}`
        : `${API}/api/security/terms`;
      
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });
      
      if (res.ok) {
        toast.success(editingTerms ? "Document updated" : "Document created");
        setShowDialog(false);
        resetForm();
        fetchData();
      } else {
        toast.error("Failed to save document");
      }
    } catch (err) {
      toast.error("Error saving document");
    }
  };

  const handleDelete = async (termsId) => {
    if (!window.confirm("Deactivate this document?")) return;
    
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/api/security/terms/${termsId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        toast.success("Document deactivated");
        fetchData();
      }
    } catch (err) {
      toast.error("Failed to deactivate document");
    }
  };

  const resetForm = () => {
    setForm({
      document_type: "terms_of_service",
      title: "",
      content: "",
      version: "1.0",
      effective_date: "",
      requires_acceptance: true,
    });
    setEditingTerms(null);
  };

  const openEdit = (doc) => {
    setEditingTerms(doc);
    setForm({
      document_type: doc.document_type || "terms_of_service",
      title: doc.title || "",
      content: doc.content || "",
      version: doc.version || "1.0",
      effective_date: doc.effective_date || "",
      requires_acceptance: doc.requires_acceptance !== false,
    });
    setShowDialog(true);
  };

  const openPreview = (doc) => {
    setPreviewContent(doc);
    setShowPreview(true);
  };

  const getTypeName = (typeId) => {
    const type = types.find(t => t.id === typeId);
    return type?.name || typeId;
  };

  const filteredTerms = terms.filter(t => {
    const matchesSearch = t.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.document_type?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeTab === "all") return matchesSearch;
    if (activeTab === "active") return matchesSearch && t.is_active;
    if (activeTab === "inactive") return matchesSearch && !t.is_active;
    return matchesSearch;
  });

  // Sample content templates
  const getTemplateContent = (type) => {
    const templates = {
      terms_of_service: `# Terms of Service

## 1. Acceptance of Terms
By accessing and using Business Genie services, you agree to be bound by these Terms of Service.

## 2. Services Description
Business Genie provides business management software services including but not limited to:
- Employee management
- Client relationship management
- Invoicing and billing
- Scheduling and calendar management

## 3. User Responsibilities
Users agree to:
- Provide accurate information
- Maintain confidentiality of account credentials
- Use services in compliance with applicable laws

## 4. Payment Terms
- All fees are due according to the agreed payment schedule
- Late payments may incur additional charges
- Refunds are subject to our refund policy

## 5. Limitation of Liability
Business Genie shall not be liable for indirect, incidental, or consequential damages.

## 6. Termination
Either party may terminate this agreement with 30 days written notice.

## 7. Governing Law
These terms are governed by applicable local laws.`,

      liability_waiver: `# Liability Waiver and Release

## Acknowledgment of Risks
I acknowledge that work performed may involve inherent risks including but not limited to:
- Working at heights
- Use of equipment and machinery
- Exposure to cleaning chemicals
- Physical labor

## Release of Liability
I hereby release, waive, and discharge Business Genie and its employees from any and all liability for injury, illness, death, or property damage arising from services provided.

## Assumption of Risk
I voluntarily assume all risks associated with the services provided.

## Indemnification
I agree to indemnify and hold harmless Business Genie from any claims arising from my actions or negligence.

## Insurance
I confirm that I maintain appropriate insurance coverage for my property.

## Signature
By accepting these terms, I acknowledge that I have read, understood, and agree to be bound by this waiver.`,

      privacy_policy: `# Privacy Policy

## Information We Collect
- Personal identification information (name, email, phone)
- Business information
- Payment information
- Usage data

## How We Use Information
- Provide and improve services
- Process transactions
- Communicate with users
- Comply with legal obligations

## Data Protection
- Encryption of sensitive data
- Access controls and authentication
- Regular security audits
- Employee training on data handling

## Your Rights
- Access your data
- Request corrections
- Request deletion
- Opt out of marketing

## Contact
For privacy concerns, contact our Data Protection Officer.`,

      service_agreement: `# Service Agreement

## Scope of Services
This agreement covers the following services:
- [Service details to be specified]

## Service Standards
- Professional quality workmanship
- Compliance with safety regulations
- Timely completion of work
- Clear communication

## Client Responsibilities
- Provide access to work areas
- Ensure safe working conditions
- Timely payment of invoices
- Communication of concerns

## Pricing and Payment
- Quoted prices are valid for 30 days
- Payment terms as specified in invoice
- Additional work billed separately

## Warranty
- Workmanship warranty: [Period]
- Material defects: [Terms]

## Cancellation Policy
- 24-hour notice required for cancellations
- Late cancellations may incur fees`,
    };
    
    return templates[type] || "";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="terms-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-7 h-7 text-primary" />
            Terms & Conditions
          </h1>
          <p className="text-muted-foreground">Manage legal documents, waivers, and policies</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="create-terms-btn">
          <Plus className="w-4 h-4 mr-2" /> New Document
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border p-4">
          <div className="text-sm text-muted-foreground">Total Documents</div>
          <div className="text-2xl font-bold">{terms.length}</div>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <div className="text-sm text-muted-foreground flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-green-500" /> Active
          </div>
          <div className="text-2xl font-bold text-green-500">{terms.filter(t => t.is_active).length}</div>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <div className="text-sm text-muted-foreground">Require Acceptance</div>
          <div className="text-2xl font-bold">{terms.filter(t => t.requires_acceptance).length}</div>
        </div>
        <div className="bg-card rounded-lg border p-4">
          <div className="text-sm text-muted-foreground">Total Acceptances</div>
          <div className="text-2xl font-bold">{terms.reduce((sum, t) => sum + (t.acceptances_count || 0), 0)}</div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search documents..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="terms-search"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({terms.length})</TabsTrigger>
          <TabsTrigger value="active">Active ({terms.filter(t => t.is_active).length})</TabsTrigger>
          <TabsTrigger value="inactive">Inactive ({terms.filter(t => !t.is_active).length})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {filteredTerms.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No documents found</p>
              <Button variant="link" onClick={() => setShowDialog(true)}>Create your first document</Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredTerms.map((doc) => (
                <div key={doc.id} className="bg-card rounded-lg border p-4" data-testid={`terms-${doc.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{doc.title}</h3>
                        <Badge variant="outline">{getTypeName(doc.document_type)}</Badge>
                        {doc.is_active ? (
                          <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                        {doc.requires_acceptance && (
                          <Badge className="bg-blue-500/20 text-blue-400">Requires Acceptance</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          Version {doc.version}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Effective: {doc.effective_date || "Not set"}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {doc.acceptances_count || 0} acceptances
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openPreview(doc)}>
                        <Eye className="w-4 h-4 mr-1" /> Preview
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(doc)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      {doc.is_active && (
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(doc.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTerms ? "Edit Document" : "Create Legal Document"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Document Type *</Label>
                <Select 
                  value={form.document_type} 
                  onValueChange={(v) => {
                    setForm({ 
                      ...form, 
                      document_type: v,
                      content: form.content || getTemplateContent(v),
                      title: form.title || types.find(t => t.id === v)?.name || ""
                    });
                  }}
                >
                  <SelectTrigger data-testid="terms-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map((type) => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Version *</Label>
                <Input
                  value={form.version}
                  onChange={(e) => setForm({ ...form, version: e.target.value })}
                  placeholder="1.0"
                  data-testid="terms-version-input"
                />
              </div>
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Document title"
                data-testid="terms-title-input"
              />
            </div>
            <div>
              <Label>Effective Date</Label>
              <Input
                type="date"
                value={form.effective_date}
                onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Content (Markdown supported) *</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Enter document content..."
                rows={15}
                className="font-mono text-sm"
                data-testid="terms-content-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Use Markdown formatting: # Heading, ## Subheading, - bullet points, **bold**, *italic*
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.requires_acceptance}
                onCheckedChange={(v) => setForm({ ...form, requires_acceptance: v })}
              />
              <Label>Require user acceptance</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!form.title || !form.content} data-testid="save-terms-btn">
              {editingTerms ? "Update" : "Create"} Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewContent?.title}</DialogTitle>
          </DialogHeader>
          <div className="prose prose-sm dark:prose-invert max-w-none">
            {previewContent?.content?.split('\n').map((line, i) => {
              if (line.startsWith('# ')) {
                return <h1 key={i} className="text-xl font-bold mt-4 mb-2">{line.substring(2)}</h1>;
              }
              if (line.startsWith('## ')) {
                return <h2 key={i} className="text-lg font-semibold mt-3 mb-1">{line.substring(3)}</h2>;
              }
              if (line.startsWith('- ')) {
                return <li key={i} className="ml-4">{line.substring(2)}</li>;
              }
              if (line.trim() === '') {
                return <br key={i} />;
              }
              return <p key={i} className="my-1">{line}</p>;
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
